import { XMarkIcon } from '@heroicons/react/24/solid';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import upperArchIcon from '../../assets/icons/upper-arch.svg';
import lowerArchIcon from '../../assets/icons/lower-arch.svg';
import bothArchsIcon from '../../assets/icons/both-archs.svg';
import type { PatientFile } from '../../services/cloud-files';
import { updateScanId } from '../../services/patient';
import FileUploadCard from '../upload/FileUploadCard';
import Spinner from '../Spinner';

type Step = 'intro' | 'upload' | 'details';
type UploadMode = 'files' | 'scanId';

interface UploadRefinementRecordsModalProps {
    isOpen: boolean;
    patientId: string | null;
    onClose: () => void;
    onComplete: (refinementGoal: string) => void;
    isSubmitting?: boolean;
}

const UploadRefinementRecordsModal: React.FC<UploadRefinementRecordsModalProps> = ({
    isOpen,
    patientId,
    onClose,
    onComplete,
    isSubmitting = false
}) => {
    const [step, setStep] = useState<Step>('intro');
    const [uploadMode, setUploadMode] = useState<UploadMode>('files');
    const [upperFiles, setUpperFiles] = useState<PatientFile[]>([]);
    const [lowerFiles, setLowerFiles] = useState<PatientFile[]>([]);
    const [biteFiles, setBiteFiles] = useState<PatientFile[]>([]);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [refinementGoal, setRefinementGoal] = useState('');
    const [scanId, setScanId] = useState('');
    const [scanIdError, setScanIdError] = useState('');
    const [isSavingScanId, setIsSavingScanId] = useState(false);

    const isUploading = uploadingCount > 0;
    const handleUploadStateChange = useCallback((_isUploading: boolean, delta: 1 | -1) => {
        setUploadingCount((prev) => Math.max(0, prev + delta));
    }, []);

    const createScanTypeFilter = useCallback((scanType: string) => {
        return (file: PatientFile) => {
            const meta = file.metadata as Record<string, unknown> | null;
            return meta?.refinement === true && meta?.scanType === scanType;
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setStep('intro');
            setUploadMode('files');
            setUpperFiles([]);
            setLowerFiles([]);
            setBiteFiles([]);
            setUploadingCount(0);
            setRefinementGoal('');
            setScanId('');
            setScanIdError('');
            setIsSavingScanId(false);
        }
    }, [isOpen]);

    const handleSaveScanId = useCallback(async () => {
        if (!patientId || !scanId.trim()) return false;
        setIsSavingScanId(true);
        setScanIdError('');
        try {
            await updateScanId(patientId, scanId.trim());
            return true;
        } catch {
            setScanIdError('Failed to save scan ID. Please try again.');
            return false;
        } finally {
            setIsSavingScanId(false);
        }
    }, [patientId, scanId]);

    if (!isOpen || !patientId) return null;

    const hasAllRequiredFiles =
        upperFiles.length > 0 && lowerFiles.length > 0 && biteFiles.length > 0;
    const canProceedUpload =
        uploadMode === 'files' ? hasAllRequiredFiles : scanId.trim().length > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        {step === 'intro' && 'Refinement approved'}
                        {step === 'upload' && 'Upload refinement records'}
                        {step === 'details' && 'Provide additional information'}
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={isUploading || isSubmitting}
                        className={`${isUploading || isSubmitting ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-800'}`}
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 pb-6 space-y-5 overflow-y-auto scrollbar-hide">
                    {step === 'intro' && (
                        <>
                            <p className="text-sm text-gray-600">
                                Your next step is to upload refinement records. If you don&apos;t
                                have these ready, you can upload them later.
                            </p>

                            <div className="bg-gray-50 rounded-xl p-6">
                                <p className="text-sm font-bold text-gray-800 mb-4">
                                    What you&apos;ll need:
                                </p>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="flex flex-col items-center gap-3 bg-white rounded-lg p-4 border border-gray-200">
                                        <img
                                            src={upperArchIcon}
                                            alt="Upper arch"
                                            className="w-20 h-20"
                                        />
                                        <span className="text-sm text-gray-700 text-center">
                                            Upper STL files
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-center gap-3 bg-white rounded-lg p-4 border border-gray-200">
                                        <img
                                            src={lowerArchIcon}
                                            alt="Lower arch"
                                            className="w-20 h-20"
                                        />
                                        <span className="text-sm text-gray-700 text-center">
                                            Lower STL files
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-center gap-3 bg-white rounded-lg p-4 border border-gray-200">
                                        <img
                                            src={bothArchsIcon}
                                            alt="Bite scans"
                                            className="w-20 h-20"
                                        />
                                        <span className="text-sm text-gray-700 text-center">
                                            Bite scans
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
                                <InformationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-blue-700 leading-relaxed">
                                    Please ensure your bite scans are high quality, preferably STL
                                    files. Accurate bite scans are{' '}
                                    <span className="font-semibold">essential</span> to correctly
                                    position the upper and lower arches and directly impact the
                                    quality and duration of your refinement treatment plan.
                                </p>
                            </div>
                        </>
                    )}

                    {step === 'upload' && (
                        <>
                            {/* Mode toggle */}
                            <div className="flex bg-gray-100 rounded-full p-1">
                                <button
                                    onClick={() => setUploadMode('files')}
                                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                                        uploadMode === 'files'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Upload STL files
                                </button>
                                <button
                                    onClick={() => setUploadMode('scanId')}
                                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                                        uploadMode === 'scanId'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Enter scan ID
                                </button>
                            </div>

                            {uploadMode === 'files' && (
                                <div className="grid grid-cols-3 gap-4">
                                    <FileUploadCard
                                        label="Upper STL"
                                        icon={upperArchIcon}
                                        patientId={patientId}
                                        fileType="STL"
                                        accept=".stl"
                                        description="Upper STL"
                                        metadata={{ refinement: true, scanType: 'upper' }}
                                        fileFilter={createScanTypeFilter('upper')}
                                        onUploadedFilesChange={setUpperFiles}
                                        onUploadStateChange={handleUploadStateChange}
                                    />
                                    <FileUploadCard
                                        label="Lower STL"
                                        icon={lowerArchIcon}
                                        patientId={patientId}
                                        fileType="STL"
                                        accept=".stl"
                                        description="Lower STL"
                                        metadata={{ refinement: true, scanType: 'lower' }}
                                        fileFilter={createScanTypeFilter('lower')}
                                        onUploadedFilesChange={setLowerFiles}
                                        onUploadStateChange={handleUploadStateChange}
                                    />
                                    <FileUploadCard
                                        label="Bite scans"
                                        icon={bothArchsIcon}
                                        patientId={patientId}
                                        fileType="STL"
                                        accept=".stl,image/*"
                                        description="Bite scan"
                                        metadata={{ refinement: true, scanType: 'bite' }}
                                        fileFilter={createScanTypeFilter('bite')}
                                        onUploadedFilesChange={setBiteFiles}
                                        onUploadStateChange={handleUploadStateChange}
                                    />
                                </div>
                            )}

                            {uploadMode === 'scanId' && (
                                <div>
                                    <input
                                        type="text"
                                        value={scanId}
                                        onChange={(e) => {
                                            setScanId(e.target.value);
                                            setScanIdError('');
                                        }}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 transition-colors"
                                        placeholder="Enter scan ID"
                                    />
                                    {scanIdError && (
                                        <p className="text-sm text-red-500 mt-2">{scanIdError}</p>
                                    )}
                                </div>
                            )}

                            <FileUploadCard
                                label="Additional files"
                                patientId={patientId}
                                fileType="ADDITIONAL"
                                accept="*"
                                isOptional
                                description="Additional"
                                metadata={{ refinement: true, scanType: 'additional' }}
                                fileFilter={createScanTypeFilter('additional')}
                                onUploadStateChange={handleUploadStateChange}
                            />
                        </>
                    )}

                    {step === 'details' && (
                        <>
                            <p className="text-sm text-gray-600">
                                Please share chart notes from your last appointment or describe the
                                current status of your patient, as well as what's prompting this
                                refinement, to help us plan it.
                                <span className="text-red-500">*</span>
                            </p>

                            <textarea
                                value={refinementGoal}
                                onChange={(e) => setRefinementGoal(e.target.value)}
                                placeholder="e.g., Patient requested a refinement due to mild crowding on lower anteriors. Upper arch tracking well. Attachments on #12 and #22 still bonded. IPR completed through step 14..."
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl placeholder-gray-400/60 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 transition-colors duration-200 resize-none h-56"
                            />
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                    {step === 'intro' && (
                        <>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-semibold rounded-lg border text-gray-800 bg-white border-gray-300 hover:bg-gray-50"
                            >
                                Upload later
                            </button>
                            <button
                                onClick={() => setStep('upload')}
                                className="px-4 py-2 text-sm font-bold rounded-lg bg-gray-800 text-white hover:bg-gray-700"
                            >
                                Upload records now
                            </button>
                        </>
                    )}

                    {step === 'upload' && (
                        <>
                            <button
                                onClick={() => setStep('intro')}
                                disabled={isUploading || isSavingScanId}
                                className={`px-4 py-2 text-sm font-semibold rounded-lg border ${
                                    isUploading || isSavingScanId
                                        ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                                        : 'text-gray-800 bg-white border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                Back
                            </button>
                            <button
                                onClick={async () => {
                                    if (uploadMode === 'scanId') {
                                        const success = await handleSaveScanId();
                                        if (!success) return;
                                    }
                                    setStep('details');
                                }}
                                disabled={!canProceedUpload || isUploading || isSavingScanId}
                                className={`px-4 py-2 text-sm font-bold rounded-lg flex items-center gap-2 ${
                                    canProceedUpload && !isUploading && !isSavingScanId
                                        ? 'bg-gray-800 text-white hover:bg-gray-700'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                {isSavingScanId && <Spinner />}
                                Next
                            </button>
                        </>
                    )}

                    {step === 'details' && (
                        <>
                            <button
                                onClick={() => setStep('upload')}
                                disabled={isSubmitting}
                                className={`px-4 py-2 text-sm font-semibold rounded-lg border ${
                                    isSubmitting
                                        ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                                        : 'text-gray-800 bg-white border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                Back
                            </button>
                            <button
                                onClick={() => onComplete(refinementGoal)}
                                disabled={!refinementGoal.trim() || isSubmitting}
                                className={`px-4 py-2 text-sm font-bold rounded-lg flex items-center gap-2 ${
                                    refinementGoal.trim() && !isSubmitting
                                        ? 'bg-gray-800 text-white hover:bg-gray-700'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                {isSubmitting && <Spinner />}
                                Submit
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UploadRefinementRecordsModal;
